import { Medicine, MedicineDosagePackageType, MedicineDosageType } from 'src/interfaces/medicine';
import { isBlankSageItemCode, readSageItemCode } from './sage-item-code';

export interface PagedResult<T> {
  results: T[];
  total: number;
}

export interface MissingSagePackageRow {
  packageId: number;
  medicineId: number;
  medicineName: string;
  medicineDosageTypeId: number;
  strength: string;
  packageType: string;
  label: string;
  pack: MedicineDosagePackageType;
}

export interface MissingSagePackageScan {
  rows: MissingSagePackageRow[];
  medicinesDone: number;
  medicineTotal: number;
  incomplete: boolean;
}

export interface MissingSagePackageScanDeps {
  listMedicines: (page: number, limit: number) => Promise<PagedResult<Medicine> | null>;
  listDosageTypes: (
    medicineId: number,
    page: number,
    limit: number
  ) => Promise<PagedResult<MedicineDosageType> | null>;
  listPackages: (
    medicineId: number,
    dosageTypeId: number,
    page: number,
    limit: number
  ) => Promise<PagedResult<MedicineDosagePackageType> | null>;
  onProgress?: (state: { medicinesDone: number; medicineTotal: number }) => void;
  isCancelled?: () => boolean;
}

const PAGE_LIMIT = 100;
const MAX_PAGES = 200;
const MEDICINE_CONCURRENCY = 4;

/**
 * Walk the existing medicine → dosage type → package list endpoints and keep
 * packs whose sage_item_code is null or blank. Does not call any other route.
 */
export async function collectPackagesMissingSageItemCode(
  deps: MissingSagePackageScanDeps
): Promise<MissingSagePackageScan> {
  const medicines = await fetchAllPages((page, limit) => deps.listMedicines(page, limit), deps);
  const medicineTotal = medicines.reportedTotal > 0 ? medicines.reportedTotal : medicines.items.length;
  const rows: MissingSagePackageRow[] = [];
  let incomplete = medicines.failed;
  let medicinesDone = 0;

  deps.onProgress?.({ medicinesDone, medicineTotal });

  await mapPool(medicines.items, MEDICINE_CONCURRENCY, async (medicine) => {
    if (deps.isCancelled?.()) return;

    const dosageTypes = await fetchAllPages(
      (page, limit) => deps.listDosageTypes(medicine.id, page, limit),
      deps
    );
    if (dosageTypes.failed) incomplete = true;

    for (const dosageType of dosageTypes.items) {
      if (deps.isCancelled?.()) return;
      const packs = await fetchAllPages(
        (page, limit) => deps.listPackages(medicine.id, dosageType.id, page, limit),
        deps
      );
      if (packs.failed) incomplete = true;

      for (const pack of packs.items) {
        if (!isBlankSageItemCode(readSageItemCode(pack))) continue;
        rows.push({
          packageId: pack.id,
          medicineId: medicine.id,
          medicineName: medicine.name?.trim() ? medicine.name : '—',
          medicineDosageTypeId: dosageType.id,
          strength: displayText(dosageType.dosage_strength),
          packageType: displayText(pack.package_type?.type),
          label: displayText(pack.label_name),
          pack,
        });
      }
    }

    medicinesDone += 1;
    deps.onProgress?.({ medicinesDone, medicineTotal });
  });

  rows.sort((a, b) => a.packageId - b.packageId);

  return {
    rows,
    medicinesDone,
    medicineTotal,
    incomplete,
  };
}

async function fetchAllPages<T>(
  load: (page: number, limit: number) => Promise<PagedResult<T> | null>,
  deps: MissingSagePackageScanDeps
): Promise<{ items: T[]; failed: boolean; reportedTotal: number }> {
  const items: T[] = [];
  let reportedTotal = 0;

  for (let page = 1; page <= MAX_PAGES; page++) {
    if (deps.isCancelled?.()) {
      return { items, failed: false, reportedTotal };
    }
    const batch = await load(page, PAGE_LIMIT);
    if (!batch) {
      return { items, failed: true, reportedTotal };
    }
    if (batch.total > reportedTotal) reportedTotal = batch.total;
    items.push(...batch.results);
    if (batch.results.length === 0 || batch.results.length < PAGE_LIMIT) {
      return { items, failed: false, reportedTotal };
    }
    if (reportedTotal > 0 && items.length >= reportedTotal) {
      return { items, failed: false, reportedTotal };
    }
  }

  return { items, failed: true, reportedTotal };
}

async function mapPool<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  if (items.length === 0) return;
  let index = 0;
  const size = Math.min(limit, items.length);

  async function run(): Promise<void> {
    while (index < items.length) {
      const current = index;
      index += 1;
      await worker(items[current]);
    }
  }

  await Promise.all(Array.from({ length: size }, () => run()));
}

function displayText(value: unknown): string {
  if (value == null) return '—';
  const text = String(value).trim();
  return text ? text : '—';
}
